import React, { useEffect, useState } from "react";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { Box, Stack, Theme } from "@mui/material";
import { FilterModal } from "../FilterModal";
import { FilterItemButton } from "../../components/Button/FilterItemButton";
import { ClientResponse, IPagination, IQueryPerams } from "../../config/types";
import { SideBarSearchInput } from "../../components/Layout/SideBarSearchInput";
import CustomizedSwitch from "../../components/Switch";
import { formatSnakeCaseToReadable } from "../../utils/common";

interface FilterProps<T> {
  ftn: (params: IQueryPerams) => Promise<ClientResponse<T>>;
  updateQuerySet: (query: React.SetStateAction<IQueryPerams>) => void;
  updateDataSet: (data: T, pagination?: IPagination) => void;
}

interface IMaintenanceScheduleFilter {
  asset_category?: string;
  asset_serial_number?: string;
  schedule_status?: string;
  search?: string;
  is_deleted?: boolean;
}

export const MaintenanceScheduleFilter = <T,>({
  ftn,
  updateQuerySet,
  updateDataSet,
}: FilterProps<T>) => {
  const [loading, setLoading] = useState(true);

  const { form, updateInputField, updateForm, onFormSubmit, getFormValues } =
    useDmsForm<IMaintenanceScheduleFilter>({
      asset_category: "",
      asset_serial_number: "",
      schedule_status: "",
      search: "",
    });

  const resetFilter = <K extends keyof IMaintenanceScheduleFilter>(key: K) => {
    updateForm((p) => {
      const state = {
        ...p,
        [key]: { value: "" },
      };
      updateQuerySet((prev) => ({ ...prev, [key]: "" }));
      return state;
    });
  };

  useEffect(() => {
    const { data } = onFormSubmit();
    updateQuerySet((p) => ({ ...p, ...data }));
    const filterRequest = async () => {
      setLoading(true);
      const res = await ftn(data);
      if (res.data && updateDataSet) {
        updateDataSet(res.data, res.pagination);
      }
      setLoading(false);
    };
    filterRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.asset_category?.value,
    form.asset_serial_number?.value,
    form.schedule_status?.value,
    form.search?.value,
    form.is_deleted
  ]);

  return (
    <Stack direction={"row"} justifyContent={"space-between"} width={"100%"}>
      <Stack direction={"row"} spacing={2}>
        <FilterModal label="Filters" loading={loading} closeOnoutSideClick>
          <Stack direction={"column"} spacing={2}>
            <SideBarSearchInput
              label="Asset Category"
              placeholder="Search by category ..."
              value={form.asset_category?.value || ""}
              handleChange={updateInputField("asset_category")}
            />
            <CustomizedSwitch
              title="deleted"
              onChange={updateInputField("is_deleted", "BOOLEAN")}
              value={form.is_deleted?.value ?? false}
            />
          </Stack>
        </FilterModal>
        <Box sx={styles.filterLayout}>
          {Object.entries(getFormValues())
            .filter(
              ([_, value]) =>
                (typeof value === "string" && value.trim() !== "") ||
                typeof value === "boolean"
            )
            .map(([key, value]) => (
              <FilterItemButton
                key={key}
                label={`By ${formatSnakeCaseToReadable(key)}`}
                tip={value.toString()}
                height={40}
                onClick={() => resetFilter(key as keyof IMaintenanceScheduleFilter)}
              />
            ))}
        </Box>
      </Stack>
      <Box width={250}>
        <SideBarSearchInput
          placeholder="Search by status ..."
          value={form.schedule_status?.value || ""}
          handleChange={updateInputField("schedule_status")}
        />
      </Box>
    </Stack>
  );
};

const styles = {
  filterLayout: (theme: Theme) => ({
    width: 1000,
    [theme.breakpoints.down("xl")]: {
      width: 570,
    },
  }),
};